import {DateTime} from 'luxon';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DateFormat} from '../../../../../libs/shared/ui/src/components/internationalization/dateFormat';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {LuxonWeekdayToCalendarWeekDays} from '../../../../helpers/models/calendarEventHelpers';
import {getWeekOfMonth, RecurrenceWeekOfMonthToText} from '../../../../helpers/models/recurringEventHelpers';
import {RecurrenceWeekdayModel} from '../../../../models/api/recurrenceModel';

/*
 * Props.
 */

export interface RecurrenceMonthlyWeekdayEditorProps {
  startDateTime: DateTime;
  recurrenceWeekdays: ReadonlyArray<RecurrenceWeekdayModel> | undefined;
  setRecurrenceWeekdays: (arg: Array<RecurrenceWeekdayModel> | undefined) => void;
}

/*
 * Styles.
 */

const StyledMonthlyDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  width: 250px;
`;

const StyledValueDiv = styled.div`
  display: grid;
`;

const StyledValueTextDiv = styled.div`
  ${ellipsis()};
`;

/*
 * Component.
 */

export const RecurrenceMonthlyWeekdayEditor: FC<RecurrenceMonthlyWeekdayEditorProps> = (props) => {
  const {startDateTime, recurrenceWeekdays, setRecurrenceWeekdays} = props;

  const jsStartDate = startDateTime;
  const weekday = LuxonWeekdayToCalendarWeekDays[startDateTime.weekday];
  const weekOfMonth = getWeekOfMonth(startDateTime);
  const weekOfMonthLong = RecurrenceWeekOfMonthToText[weekOfMonth];

  const options = [
    {
      // e.g. Monthly on day 6
      key: 'absolute-monthly',
      byweekday: undefined,
      text: (
        <>
          <Trans id="custom-recurrence-absolute-monthly">Monthly on day</Trans>{' '}
          <DateFormat value={jsStartDate} format={{day: 'numeric'}} />
        </>
      ),
      isSelected: recurrenceWeekdays === undefined,
    },
    {
      // e.g. Monthly on the second tuesday
      key: 'relative-monthly',
      byweekday: {weekday, n: weekOfMonth},
      text: (
        <>
          <Trans id="custom-recurrence-relative-monthly">Monthly on the</Trans> {weekOfMonthLong}{' '}
          <DateFormat value={jsStartDate} format={{weekday: 'long'}} />
        </>
      ),
      isSelected: recurrenceWeekdays !== undefined,
    },
  ];

  const selectedOption = options.find((option) => option.isSelected);
  return (
    <StyledMonthlyDropdownBoxCoordinator
      value={
        <StyledValueDiv>
          <StyledValueTextDiv>{selectedOption ? selectedOption.text : undefined}</StyledValueTextDiv>
        </StyledValueDiv>
      }
      renderDropdown={(popoverProps) => renderMonthlyDropdown(popoverProps, options, setRecurrenceWeekdays)}
    />
  );
};

type MonthlyOption = {
  key: string;
  byweekday: RecurrenceWeekdayModel | undefined;
  text: ReactNode;
  isSelected: boolean;
};

function renderMonthlyDropdown(
  popoverProps: PopoverTransferProps,
  options: Array<MonthlyOption>,
  setRecurrenceWeekdays: (arg: Array<RecurrenceWeekdayModel> | undefined) => void,
) {
  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        {options.map((option) => (
          <DropdownItem
            key={option.key}
            isChecked={option.isSelected}
            onSelect={() => setRecurrenceWeekdays(option.byweekday ? [option.byweekday] : undefined)}
          >
            <DropdownItemContent isChecked={option.isSelected}>{option.text}</DropdownItemContent>
          </DropdownItem>
        ))}
      </DropdownList>
    </Dropdown>
  );
}
